import * as d3 from 'd3';
import addAxisHover from '../../lib/addAxisHover';

export default {
  create(
    el,
    data,
    {
      axisLabels = { x: 'X Axis', y: 'Y Axis' },
      quadrantLabels = {},
      animate = true,
      topLeftColour = '#ffdfdf',
      axisHoverText = {},
    },
  ) {
    const outerWidth = el.getBoundingClientRect().width;
    const scaledHeight = 400;
    const minXVal = d3.min(data, (d) => d.x);
    const maxXVal = d3.max(data, (d) => d.x);
    const minYVal = d3.min(data, (d) => d.y);
    const maxYVal = d3.max(data, (d) => d.y);

    const margin = { top: 50, right: 30, bottom: 50, left: 40 },
      width = outerWidth - margin.left - margin.right,
      height = scaledHeight - margin.top - margin.bottom;

    this.chart = d3
      .select(el)
      .attr('width', width + margin.left + margin.right)
      .attr('height', scaledHeight + margin.top + margin.bottom)
      .attr('viewBox', '0 0 ' + outerWidth + ' ' + (scaledHeight + margin.top + margin.bottom))
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    const chart = this.chart;

    const x = d3
      .scaleLinear()
      .domain([minXVal, maxXVal])
      .range([0 + 30, width - 30]);

    const y = d3
      .scaleLinear()
      .domain([minYVal, maxYVal])
      .range([height - 30, 0 + 30]);

    // Quadrant lines
    chart
      .append('line')
      .attr('x1', 0)
      .attr('y1', height / 2)
      .attr('x2', width)
      .attr('y2', height / 2)
      .attr('stroke-width', 0.5)
      .attr('stroke', '#999');

    chart
      .append('line')
      .attr('x1', width / 2)
      .attr('y1', 0)
      .attr('x2', width / 2)
      .attr('y2', height)
      .attr('stroke-width', 0.5)
      .attr('stroke', '#999');

    // Quadrant labels
    const quadrantLabelPositions = {
      topLeft: { x: width / 4, y: 0, dy: -20 },
      topRight: { x: (width / 4) * 3, y: 0, dy: -20 },
      bottomLeft: { x: width / 4, y: height, dy: 20 },
      bottomRight: { x: (width / 4) * 3, y: height, dy: 20 },
    };

    if (topLeftColour) {
      chart
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', width / 2)
        .attr('height', height / 2)
        .attr('fill', topLeftColour);
    }

    Object.keys(quadrantLabels).forEach((key) => {
      if (quadrantLabels[key]) {
        chart
          .append('text')
          .attr('x', quadrantLabelPositions[key].x)
          .attr('y', quadrantLabelPositions[key].y)
          .attr('dx', quadrantLabelPositions[key].dx)
          .attr('dy', quadrantLabelPositions[key].dy)
          .attr('class', 'chart__label prio-matrix__quadrant-label')
          .attr('text-anchor', 'middle')
          .text(quadrantLabels[key]);
      }
    });

    //Axis
    this.xAxis = chart
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', height)
      .attr('stroke-width', 1)
      .attr('stroke', 'black')
      .attr('class', 'x-axis-line');

    chart
      .append('text')
      .attr('x', 0)
      .attr('y', height)
      .attr('dy', 15)
      .attr('class', 'chart__label')
      .attr('text-anchor', 'start')
      .text(this.roundToTwo(x.invert(0)));

    chart
      .append('text')
      .attr('x', width)
      .attr('y', height)
      .attr('dy', 15)
      .attr('class', 'chart__label')
      .attr('text-anchor', 'end')
      .text(this.roundToTwo(x.invert(width)));

    let yAxisHover = null;

    chart
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('class', 'chart__label chart__axis-label')
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(0, ' + height / 2 + ') rotate(-90)')
      .attr('dy', -10)
      .text(axisLabels.y)
      .on('mouseover', () => {
        yAxisHover.attr('visibility', 'visible');
      })
      .on('mouseout', () => {
        yAxisHover.attr('visibility', 'hidden');
      });

    if (axisHoverText.y) {
      yAxisHover = addAxisHover(chart, axisHoverText.y, {
        x: 20,
        y: height / 2,
      });
    }

    chart
      .append('line')
      .attr('x1', 0)
      .attr('y1', height)
      .attr('x2', width)
      .attr('y2', height)
      .attr('stroke-width', 1)
      .attr('stroke', 'black');

    chart
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('dx', -5)
      .attr('dy', 9)
      .attr('class', 'chart__label')
      .attr('text-anchor', 'end')
      .text(this.roundToTwo(y.invert(0)));

    chart
      .append('text')
      .attr('x', 0)
      .attr('y', height)
      .attr('dx', -5)
      .attr('class', 'chart__label')
      .attr('text-anchor', 'end')
      .text(this.roundToTwo(y.invert(height)));

    const xAxisText = chart
      .append('text')
      .attr('x', width / 2)
      .attr('y', height)
      .attr('dy', quadrantLabels.bottomLeft || quadrantLabels.bottomRight ? 40 : 20)
      .attr('class', 'chart__label chart__axis-label')
      .attr('text-anchor', 'middle')
      .text(axisLabels.x);

    chart
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', width)
      .attr('y2', 0)
      .attr('stroke-width', 1)
      .attr('stroke', 'black');

    this.rightBorder = chart
      .append('line')
      .attr('x1', width)
      .attr('y1', 0)
      .attr('x2', width)
      .attr('y2', height)
      .attr('stroke-width', 1)
      .attr('stroke', 'black');

    // Datapoints
    const point = chart
      .selectAll('.data-item')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'data-item');

    point.each(function (d) {
      let el = d3.select(this);
      if ('link' in d) {
        el = el.append('svg:a').attr('href', d.link);
      }

      const circle = el
        .append('circle')
        .attr('class', 'point')
        .attr('cx', (d) => x(d.x))
        .attr('cy', (d) => y(d.y))
        .attr('r', 5)
        .attr('fill', (d) => {
          if ('highlight' in d && d.highlight) {
            return '#1254a6';
          }
          return '#f59936';
        });

      if (animate) {
        circle.style('opacity', 0).transition().delay(800).style('opacity', 1);
      }

      const text = el
        .append('text')
        .attr('x', (d) => x(d.x))
        .attr('y', (d) => y(d.y))
        .attr('dy', '20')
        .attr('class', 'chart__label point-label')
        .attr('text-anchor', 'middle')
        .text((d) => d.label);

      if (animate) {
        text.style('opacity', 0).transition().delay(800).style('opacity', 1);
      }
    });

    // Lable positioning adjustments
    this.repositionOverlappingLabels();
    this.repositionOverhangingLabels();

    if (axisHoverText.x) {
      let xAxisHover = null;

      xAxisText
        .on('mouseover', () => {
          const bbox = xAxisHover.node().getBBox();

          xAxisHover
            .attr(
              'transform',
              'translate(' + (width / 2 - bbox.width / 2) + ', ' + (height - bbox.height) + ')',
            )
            .attr('visibility', 'visible');
        })
        .on('mouseout', () => {
          xAxisHover.attr('visibility', 'hidden');
        });

      xAxisHover = addAxisHover(chart, axisHoverText.x, {
        x: width / 2,
        y: height,
      });
    }
  },

  repositionOverhangingLabels() {
    const chart = this.chart;
    const labels = chart.selectAll('.point-label, .point');
    const xAxisBBox = this.xAxis.node().getBBox();
    const rightBorderBBox = this.rightBorder.node().getBBox();

    labels.each(function () {
      const labelBBox = this.getBBox();

      const leftOverlap = labelBBox.x < xAxisBBox.x;
      const rightOverlap = labelBBox.x + labelBBox.width > rightBorderBBox.x;

      if (leftOverlap || rightOverlap) {
        // If label overhangs x axis, try moving it over

        if (leftOverlap) {
          d3.select(this).attr('x', 0);
          d3.select(this).attr('text-anchor', 'start');
        }

        if (rightOverlap) {
          d3.select(this).attr('x', rightBorderBBox.x - 10);
          d3.select(this).attr('text-anchor', 'end');
        }

        d3.select(this).attr('dx', null);
        d3.select(this).attr('dy', 20);

        // If moving the overlapping label has caused it to
        // overlap another label, try positioning it above marker
        const nodeA = this;
        labels.each(function () {
          const nodeB = this;
          if (
            nodeA !== nodeB &&
            doesOverlap(nodeA.getBoundingClientRect(), nodeB.getBoundingClientRect())
          ) {
            d3.select(nodeA).attr('dy', -10);
          }
        });

        // If positioning marker above marker still overlaps trim
        // the label content until it doesn't and provide a hover
        labels.each(function () {
          const nodeB = this;

          if (
            nodeA !== nodeB &&
            doesOverlap(nodeA.getBoundingClientRect(), nodeB.getBoundingClientRect())
          ) {
            const g = d3.select(nodeA.parentElement);
            // Move element to end so it will be displayed on top
            chart.append(() => g.node());

            const flyout = g
              .classed('trimmed-label', true)
              .append('g')
              .attr('class', 'trimmed-label-flyout');

            const labelBBox = nodeA.getBBox();

            flyout
              .append('rect')
              .attr('fill', 'white')
              .attr('rx', '0.5%')
              .attr('width', labelBBox.width + 4)
              .attr('height', labelBBox.height)
              .attr('x', labelBBox.x - 2)
              .attr('y', labelBBox.y);

            flyout.append(() => nodeA.cloneNode(true)).style('opacity', 1);

            // Keep trimming label length until it no longer overlaps
            while (doesOverlap(nodeA.getBoundingClientRect(), nodeB.getBoundingClientRect())) {
              nodeA.textContent = nodeA.textContent.replace('...', '');

              if (nodeA.textContent.length == 0) {
                nodeA.textContent = '*';
                break;
              }

              nodeA.textContent =
                nodeA.textContent.substring(0, nodeA.textContent.length - 1).trim() + '...';
            }
          }
        });
      }
    });
  },

  repositionOverlappingLabels() {
    let iteration = 0;
    while (this.moveOverlappingLabels()) {
      if (iteration >= 3) break;
      iteration++;
    }
  },

  moveOverlappingLabels() {
    let adjusted = false;

    const adjustments = [
      (node) => {
        d3.select(node).attr('text-anchor', 'end').attr('dx', 10).attr('dy', 5);
      },
      (node) => {
        d3.select(node).attr('text-anchor', 'end').attr('dx', -10).attr('dy', 5);
      },
      (node) => {
        d3.select(node).attr('text-anchor', 'middle').attr('dx', 0).attr('dy', -10);
      },
      (node) => {
        d3.select(node).attr('text-anchor', 'middle').attr('dx', 0).attr('dy', 20);
      },
    ];

    const chart = this.chart;

    chart
      .selectAll('.point-label')
      .nodes()
      .forEach((nodeA) => {
        chart
          .selectAll('.point-label, .point')
          .nodes()
          .forEach((nodeB) => {
            if (nodeA != nodeB) {
              let iteration = 0;
              let round = 0;
              while (doesOverlap(nodeA.getBoundingClientRect(), nodeB.getBoundingClientRect())) {
                adjusted = true;
                adjustments[iteration](nodeA);
                iteration++;
                if (iteration >= adjustments.length) {
                  if (round >= 3) {
                    break;
                  } else {
                    iteration = 0;
                    round++;
                  }
                }
              }
            }
          });
      });

    return adjusted;
  },

  applyStyles(el, styles) {
    const selection = d3.select(el);
    Object.keys(styles).forEach((attr) => {
      selection.attr(attr, styles[attr]);
    });
  },

  empty(el) {
    this.chart = null;
    const parent = d3.select(el);
    parent.selectAll('*').remove();
  },

  roundToTwo(num) {
    return +(Math.round(num + 'e+2') + 'e-2');
  },
};

function doesOverlap(nodeA, nodeB) {
  return (
    nodeA.left < nodeB.right &&
    nodeA.right > nodeB.left &&
    nodeA.top < nodeB.bottom &&
    nodeA.bottom > nodeB.top
  );
}
